import { useEffect } from "react";
import { z } from "zod";
import { MiDetailFields, MiModal } from "@miplatform/components";
import {
  AUTOCOMPLETE_REASON,
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  INPUT_TYPES,
  SCHEDULE_OFFSET_REFERENCES_OPTIONS,
} from "@miplatform/constants";
import { useEdit } from "@miplatform/hooks";
import { toKebabCase } from "@miplatform/utils";
import { MenuItem } from "@mui/material";
import { useAddAssignmentTemplateModal } from "./useAddAssignmentTemplateModal";

const initialData = {
  name: "",
  scheduleOffsetReference: "",
  scheduleOffsetDays: 0,
};

export function OrderTemplateAssignmentTemplateModal({
  item,
  open,
  getAllAssignmentTemplates,
  title,
  buttonTitle,
  onClose,
  loading,
  onSubmit = () => null,
}) {
  const { templates, findTemplate, getAssignmentTemplates } =
    useAddAssignmentTemplateModal({
      getAllAssignmentTemplates,
    });

  useEffect(() => {
    if (open && !item) {
      getAssignmentTemplates();
    }
  }, [open]);

  const edit = useEdit(item || initialData, []);

  const fields = [
    {
      label: "Assignment Template",
      name: "name",
      fieldType: INPUT_TYPES.AUTOCOMPLETE,
      options: templates,
      disableClearable: true,
      renderOption: (props, { value, key }) => (
        <MenuItem
          {...props}
          key={key}
          data-testid={`template-option-${toKebabCase(value)}`}
        >
          {value}
        </MenuItem>
      ),
      fullWidth: true,
      width: "100%",
      required: true,
      readOnly: !!item,
      validationRule: z.string().min(1, "Template is required"),
      handleChange: (event, option) => {
        const value = option?.value || "";
        edit.update({ name: value });
      },
      onInputChange: (event, value, reason) => {
        if (reason === AUTOCOMPLETE_REASON.CLEAR) {
          edit.update({ name: "" });
        } else if (reason === AUTOCOMPLETE_REASON.INPUT) {
          edit.update({ name: value || "" });
        }
      },
    },
    {
      label: "Trigger InProgress Order Status",
      name: "triggersInProgressOrderStatus",
      width: "100%",
      padding: 0,
      fieldType: INPUT_TYPES.CHECKBOX,
    },
    {
      label: "Trigger Complete Order Status",
      name: "triggersCompleteOrderStatus",
      width: "100%",
      padding: 0,
      fieldType: INPUT_TYPES.CHECKBOX,
    },
    {
      label: "Offset Reference Date",
      name: "scheduleOffsetReference",
      required: true,
      fieldType: INPUT_TYPES.SELECT,
      validationRule: z.string().min(1, "Offset Reference Date is required"),
      options: SCHEDULE_OFFSET_REFERENCES_OPTIONS,
      width: "100%",
    },
    {
      label: "Schedule Offset Days",
      name: "scheduleOffsetDays",
      required: true,
      fieldType: INPUT_TYPES.NUMBER,
      validationRule: z.coerce
        .number({ invalid_type_error: "Schedule Offset Days is required" })
        .min(0, "Schedule Offset Days must be at least 0"),
      width: "100%",
    },
  ];

  edit.setValidation(fields);

  const handleModalClose = () => {
    onClose();
    edit.reset();
  };

  const onAddTemplate = () => {
    if (item) {
      onSubmit({
        name: item.name,
        ...edit.edits,
        modifiedOn: item?.modifiedOn,
      });
      handleModalClose();
      return;
    }

    const { id: assignmentTemplateId } = findTemplate(edit.getValue("name"));

    if (assignmentTemplateId) {
      onSubmit({
        assignmentTemplateId,
        modifiedOn: item?.modifiedOn,
        ...edit.getValue(),
      });
    }

    handleModalClose();
  };

  return (
    <MiModal
      open={open}
      closeModal={handleModalClose}
      actions={[
        {
          name: "Cancel",
          variant: BUTTON_VARIANTS.TEXT,
          size: "medium",
          color: BUTTON_COLORS.BLACK,
          onClick: handleModalClose,
        },
        {
          name: buttonTitle,
          variant: BUTTON_VARIANTS.CONTAINED,
          size: "medium",
          color: BUTTON_COLORS.PRIMARY,
          onClick: onAddTemplate,
          disabled: loading || !edit.isValid(),
        },
      ]}
      title={title}
      topRightClose={handleModalClose}
      loading={loading}
    >
      <MiDetailFields detailFields={fields} edit={edit} />
    </MiModal>
  );
}
